/**
 * جستجوی صفحه‌ی کشف — لحظه‌ای و آفلاین.
 *
 * هیچ کوئریِ سروری این‌جا نیست و عمداً: فهرستِ کاملِ رخدادها و مکان‌ها از قبل
 * توی کشِ رویِ گوشی نشسته (نگاه کن به core/lib/offlineCache.ts)، پس جستجو
 * فقط یه فیلترِ حافظه‌ایه — بدونِ اینترنت هم کار می‌کنه و بینِ تایپ و دیدنِ
 * نتیجه هیچ رفت‌وبرگشتی نیست.
 *
 * منطقِ خالص و بی‌وابستگی به React تا بشه تست‌ش کرد (نگاه کن به search.test.ts).
 *
 * دو تصمیمِ اصلی:
 *
 * ۱. **همه‌ی متنِ رکورد**، نه چند فیلدِ دستچین‌شده. عنوان، توضیحات، آدرس،
 *    شهر، نامِ محل، یادداشتِ قیمت، آدرسِ منبع… هر رشته‌ای که توی کش هست.
 *    فهرستِ سفید معنیش این بود که هر ستونِ تازه‌ای که فردا اضافه شه،
 *    بی‌سروصدا از جستجو جا می‌مونه.
 *
 * ۲. **متنِ فارسی یه شکلِ واحد نداره.** «كافه» با کافِ عربی و «کافه» با کافِ
 *    فارسی دو رشته‌ی متفاوتن، «کافه‌شهر» یه نیم‌فاصله وسطش داره که کاربر موقعِ
 *    تایپ نمی‌ذاره، و عددها هم فارسی‌ان هم عربی هم لاتین. پس هر دو طرفِ
 *    مقایسه اول به یه شکلِ واحد در می‌آن.
 */

/**
 * نویسه‌هایی که فقط شکل‌ان و معنی‌ای اضافه نمی‌کنن: اعرابِ عربی (ً-ٕ،
 * ٰ)، کشیده (ـ)، و نویسه‌های نادیدنیِ جهت‌دهی (​-‏،
 * ⁦-⁩، ؜). نیم‌فاصله (‌) هم همین‌جاست — «کافه‌شهر» و
 * «کافه شهر» و «کافهشهر» باید یه چیز حساب شن.
 */
const DROP = /[ً-ٰٕـ؜​-‏⁦-⁩]/;

/** ده رقمِ یه خطِ عددی، از رقمِ صفرش. */
const digits = (zero: string): [string, string][] =>
  Array.from({ length: 10 }, (_, i) => [String.fromCharCode(zero.charCodeAt(0) + i), String(i)]);

/**
 * شکل‌های چندگانه‌ی یه حرف، همه به یکی.
 *
 * حرف‌های لاتینِ نشانه‌دار هم هستن (café ↔ cafe) — عمداً دستی و نه با
 * `normalize('NFD')`: اون به جدولِ یونیکدِ موتور وابسته‌ست و روی Hermes
 * تضمینی نداره، این‌جا رفتار همونیه که توی تست نوشته شده.
 */
const FOLD: Record<string, string> = Object.fromEntries([
  ['ي', 'ی'],   // ي عربی → ی فارسی
  ['ك', 'ک'],   // ك عربی → ک فارسی
  ['أ', 'ا'], ['إ', 'ا'], ['آ', 'ا'], ['ٱ', 'ا'],
  ['ة', 'ه'], ['ۀ', 'ه'],
  ['ؤ', 'و'], ['ئ', 'ی'],
  ...digits('۰'),    // ۰۱۲…
  ...digits('٠'),    // ٠١٢…
  ['á', 'a'], ['à', 'a'], ['â', 'a'], ['ä', 'a'], ['ã', 'a'], ['å', 'a'],
  ['é', 'e'], ['è', 'e'], ['ê', 'e'], ['ë', 'e'],
  ['í', 'i'], ['ì', 'i'], ['î', 'i'], ['ï', 'i'], ['ı', 'i'],
  ['ó', 'o'], ['ò', 'o'], ['ô', 'o'], ['ö', 'o'], ['õ', 'o'], ['ø', 'o'],
  ['ú', 'u'], ['ù', 'u'], ['û', 'u'], ['ü', 'u'],
  ['ñ', 'n'], ['ç', 'c'], ['ý', 'y'], ['ÿ', 'y'], ['š', 's'], ['ž', 'z'],
  ['ß', 'ss'], ['æ', 'ae'], ['œ', 'oe'],
]);

/** یه رشته به شکلِ واحدش: کوچک، بی‌اعراب، با حرف‌های یکدست، بدونِ فاصله‌ی اضافه. */
export function normalize(s: string): string {
  let out = '';
  for (const ch of s.toLowerCase()) {
    if (DROP.test(ch)) continue;
    out += FOLD[ch] ?? ch;
  }
  return out.replace(/\s+/g, ' ').trim();
}

/**
 * ستون‌هایی که متن‌ان ولی خواندنی نیستن: شناسه‌ی یکتا، هشِ محتوا، آدرسِ عکس،
 * و برچسب‌های ماشینی مثل `status` و `kind`.
 *
 * بدونِ این، جستجوی «pub» همه‌ی رکوردها رو برمی‌گردوند (`published`) و چند
 * حرفِ تصادفی به یه UUID می‌خورد.
 */
const SKIP_KEYS = new Set([
  'id', 'rootId', 'mergedInto', 'contentHash', 'imageUrl',
  'editedBy', 'reviewedBy', 'status', 'kind',
]);

/** تنها عددی که کاربر واقعاً دنبالش می‌گرده — شناسه‌ی کوتاهِ روی کارتِ ادمین. */
const NUMBER_KEYS = new Set(['shortId']);

/**
 * همه‌ی متنِ یه رکورد، آماده‌ی مقایسه.
 *
 * فاصله‌ها **حذف** می‌شن، نه اینکه بمونن: با نیم‌فاصله‌ی حذف‌شده «کافه‌شهر»
 * می‌شه «کافهشهر» و اگه فاصله‌ها می‌موندن، تایپِ «کافه شهر» دیگه پیداش
 * نمی‌کرد. خودِ کوئری هم به کلمه‌ها شکسته می‌شه (نگاه کن به queryTokens)، پس
 * ترتیبِ کلمه‌ها آزاد می‌مونه.
 *
 * `extra` برای متنیه که توی خودِ رکورد نیست ولی مالِ اونه — مثلاً نامِ
 * دسته‌ها که از جدولِ tags می‌آد.
 */
export function searchTextOf(item: unknown, extra?: string | null): string {
  const parts: string[] = [];

  const walk = (value: unknown, key: string, depth: number) => {
    if (value == null || depth > 3 || SKIP_KEYS.has(key)) return;
    if (typeof value === 'string') { parts.push(value); return; }
    if (typeof value === 'number') {
      if (NUMBER_KEYS.has(key)) parts.push(String(value));
      return;
    }
    if (Array.isArray(value)) { value.forEach((v) => walk(v, key, depth + 1)); return; }
    if (typeof value === 'object') {
      for (const [k, v] of Object.entries(value)) walk(v, k, depth + 1);
    }
  };

  walk(item, '', 0);
  if (extra) parts.push(extra);
  return normalize(parts.join(' ')).replace(/ /g, '');
}

/**
 * کوئری به کلمه‌هاش. آرایه‌ی خالی یعنی «جستجویی در کار نیست».
 *
 * چند کلمه با هم «و» ان نه «یا»: «قهوه اتاوا» یعنی هر دو توی همون رکورد
 * باشن، وگرنه هر کلمه‌ی دومی نتیجه رو **بیشتر** می‌کرد.
 */
export function queryTokens(q: string): string[] {
  return normalize(q).split(' ').filter(Boolean);
}

/** رکورد با همه‌ی کلمه‌های کوئری جور در می‌آد؟ کوئریِ خالی یعنی همه. */
export function matchesTokens(text: string, tokens: string[]): boolean {
  return tokens.every((tok) => text.includes(tok));
}
